<template>
<div class="chapter">
<div class="inner_page">
<h2 class="ph">V.</h2>
<p class="calibre3">Kinnison, the author, toiled manfully at his epic of space whenever
he was under any sort of observation, and enough at other times
to avert any suspicion. Indeed, he worked as much as Sybly Whyte,
an advertisedly temperamental writer, had ever worked. Besides
interviewing the high and the low, and taking notes everywhere, he
attended authors' teas, at which he cursed his characters fluently and
bitterly for their failure to co-operate with him. With short-haired
women and long-haired men he bemoaned the perversity of a public which
compelled them to prostitute the real genius of which each was the
unique possessor. He sympathized particularly with a fat woman writer
of whodunits, whose extremely unrealistic yet amazingly popular Gray
Lensman hero had lived through ten full-length novels and twenty
million copies.</p>
<p class="calibre3">Even though her real field was the drama, she wasn't writing the kind
of detective tripe that most of these crank-turners ground out, she
confided to Kinnison. She had known lots of Gray Lensmen <i class="calibre9">very</i>
intimately, and <i class="calibre9">her</i> stories were drawn from real life in every
particular!</p>
<p class="calibre3">Thus Kinnison remained in character; and thus he was enabled to work
completely unnoticed at his real job of finding out what was going on,
how the Boskonians were operating to ruin Radelix as they had ruined
Antigan IV.</p>
<p class="calibre3">His first care was to investigate the planet's president. That took
doing, but he did it. He examined that mind line by line and channel
by channel, with no results whatever. No scars, no sign of tampering.
Calling in assistance, he searched the president's past even more
rigidly than Fossten had searched that of Traska Gannel. Still no
soap. Everything checked, even to widely distributed boyhood pictures.
Boring from within, then, was out. His first hypothesis was wrong; this
invasion and this sabotage were being done from without. How?</p>
<p class="calibre3">Those first leaflets were followed by others, each batch more
vitriolic in tone than the preceding one. Apparently they came from
empty stratosphere; at least, no ships were to be detected in the
neighborhood after any shower of the hand-bills had appeared. But that
was not surprising. With its inertialess drive any spaceship could have
been parsecs away before the papers touched atmosphere. Or they could
have been bombed in from almost any distance. Or, as Kinnison thought
most reasonable, they could have been simply dumped out of the mouth
of a hyperspatial tube. In any event the method was immaterial. The
results only were important; and those results, the Lensman discovered,
were entirely disproportionate to the ostensible causes. The subversive
literature had some effect, of course, but essentially it must be a
blind. No possible tonnage of anonymous printing could cause that much
sheer demoralization.</p>
<p class="calibre3">Crackpot societies of all kinds sprang up everywhere, advocating
everything from absolutism to anarchy. Queer cults arose, preaching
free love, the imminent end of the world, and almost every other
conceivable departure from the norm of thought. The Authors' League,
of course, was affected more than any other organization of its size,
because of its relatively large content of strong and intensely
opinionated minds. Instead of becoming one radical group it split into
a dozen.</p>
<p class="calibre3">Kinnison joined one of those "Down with Everything!" groups, not as
a leader, but as a follower. Not too sheeplike a follower, but just
inconspicuous enough to retain his invisibly average status; and from
his place of concealment in the middle of the front rank he studied the
minds of each of his fellow anarchists. He watched those minds change,
he found out who was doing the changing. When Kinnison's turn came he
was all set for trouble. He expected to battle a powerful mentality. He
would not have been overly surprised to encounter another mad Arisian,
hiding behind a zone of hypnotic compulsion. He expected anything,
in fact, except what he found—which was a very ordinary Radeligian
therapist. The guy was a clever enough operator, of course, but he
could not work against even the feeblest opposition. Hence the Gray
Lensman had no trouble at all, either in learning everything the fellow
knew or, upon leaving him, in implanting within his mind the knowledge
that he had made Sybly Whyte into exactly the type of anarchist desired.</p>
<p class="calibre3">The trouble was that the therapist didn't know a thing. This not
entirely unexpected development posed Kinnison three questions. Did
the higher-ups ever communicate with such small fry, or did they just
give them one set of orders and cut them loose? Should he stay in
this Radeligian's mind until he found out? If he was in control of
the therapist when a big shot took over, did he have jets enough to
keep from being found out? Risky business; better scout around first,
anyway. He'd do a flit.</p>
<p class="calibre3">He drove his black speedster a million miles. He covered Radelix like a
blanket, around the equator and from pole to pole. Everywhere he found
the same state of things. The planet was literally riddled with the
agitators; he found so many that he was forced to a black conclusion.
There could be no connection or communication between such numbers of
saboteurs and any higher authority. They must have been sent with one
set of do-or-die instructions—whether they did or died was immaterial.
Experimentally, Kinnison had a few of the ringleaders taken into
custody. As each was arrested another took his place.</p>
<p class="calibre3">Martial law was finally declared, but this measure succeeded only in
driving the conspirators underground. What the subversive societies
lost in numbers they more than made up in desperation and violence.
Crime raged unchecked and uncheckable, murder became an everyday
commonplace, insanity waxed rife. And Kinnison, knowing now that no
channel to important prey would be opened until the climax, watched
grimly while the rape of the planet went on.</p>
<p class="calibre3">The president of Radelix and Lensman Gerrond sent message after
message to Prime Base and to Klovia, imploring help. The replies
to these pleas were all alike. The matter had been referred to the
Galactic Council and to the Co-ordinator. Everything that could be done
was being done. Neither office would say anything else, except that,
with the galaxy in such a disturbed condition, each planet must do its
best to solve its own problems.</p>
<p class="calibre3">The thing built up toward its atrocious finale. Gerrond invited the
president to a conference in a downtown hotel room, and there, eyes
glancing from moment to moment at the dials of a complete little
test-kit held open upon his lap:</p>
<p class="calibre3">"I have just had some startling news, sir," Gerrond said, abruptly.
"Kinnison has been here on Radelix for weeks."</p>
<p class="calibre3">"What? Kinnison? Where is he? Why didn't he—?"</p>
<p class="calibre3">"Yes, Kinnison. Kinnison of Klovia. The Co-ordinator himself. I don't
know where he is, or was. I didn't ask him." The Lensman smiled
fleetingly. "One doesn't, you know. He discussed the situation with me
at length. I am still amazed—"</p>
<p class="calibre3">"Why doesn't he stop it, then?" the president demanded. "Or can't he
stop it?"</p>
<p class="calibre3">"That's what I've got to explain to you. He can, but the time won't be
ripe until the last act."</p>
<p class="calibre3">"Why not? I tell you, if this thing can be stopped it's <i class="calibre9">got</i> to
be stopped, and no matter what has to be done it's <i class="calibre9">got</i> to be—"</p>
<p class="calibre3">"Just a minute!" Gerrond snapped. "I know that you're out of control—I
don't like to see Radelix torn apart any better than you do—but you
ought to know by this time that Galactic Co-ordinator Kimball Kinnison
is in a better position to know what to do than any other man in the
universe. Furthermore, his word is the last word. What he says, goes."</p>
<p class="calibre3">"Of course," the president apologized. "I am overwrought ... but to see
our entire world pulled down around us and upon us, our institutions,
the work of centuries, destroyed, millions of lives lost ... all
needlessly—"</p>
<p class="calibre3">"It won't come to that, he says, if we all do our parts. And you, sir,
are very much in the picture."</p>
<p class="calibre3">"I? How?"</p>
<p class="calibre3">"Are you familiar with exactly what happened upon Antigan IV?"</p>
<p class="calibre3">"Why, no. They had some trouble over there, I recall, but—"</p>
<p class="calibre3">"That's it. That's why this must go on. No planet cares particularly
about what happens to any other planet, but the Co-ordinator cares
about them all, as a whole. If this trouble is headed off now, it will
simply spread to other planets; if it is allowed to come to a climax
there is a good chance that we can put an end to the whole trouble, for
good."</p>
<p class="calibre3">"But what has that to do with me? What can I, personally, do?"</p>
<p class="calibre3">"Much. The last act upon Antigan IV, the thing that made it a planet
of maniacs, was the kidnaping of Planetary President Renwood. It is
supposed that he was murdered, since no trace of him has ever been
found."</p>
<p class="calibre3">"Oh." The older man's hands clenched, then loosened. "I am willing ...
provided—Is the Co-ordinator fairly certain that my death will enable
him—"</p>
<p class="calibre3">"It won't get that far, sir. He intends to stop it just before that.
He and his associates—I don't know who they are—have been listing
every enemy agent they can find, and they will all be taken care of at
once. He believes that Boskone will publish in advance a definite time
at which they will take you away from us. That was the way it went at
Antigan."</p>
<p class="calibre3">"Even from the Patrol?"</p>
<p class="calibre3">"From Base itself. Co-ordinator Kinnison is pretty sure that they can
do it, except for something that he can bring into play only at the
last moment. Incidentally, that is why we are having this meeting here,
with this detector which he gave me. He is afraid that Base is porous."</p>
<p class="calibre3">"In that case ... what can he—" The president fell silent.</p>
<p class="calibre3">"All that I know is that we are to dress you in a certain suit of armor
and have you in my private office in Base a few minutes before the time
they set. We and the guards leave the office at minus two minutes and
walk down the corridor, just fast enough so that at minus one minute we
are exactly in front of Room Twenty-four. We are to rehearse it until
our timing is perfect. I have no idea what is going to happen then, but
I know that something will. We are not to discuss this again, even
via Lens, as he is pretty sure that you will very shortly be under
surveillance every minute."</p>
<p class="calibre3">Time passed; the Boskonian infiltration progressed strictly according
to plan. Upon the surface it appeared that Radelix was going in almost
the same fashion in which Antigan IV had gone. Below the surface,
however, there was one great difference. Every ship, whether liner or
freighter or tramp, which docked at any spaceport of Radelix, brought
at least one man who did not leave. Some of these visitors were tall
and lithe, some were short and fat. Some were old, some were young.
Some were pale, some were burned to the complexion of ancient leather
by the fervent rays of space. They were alike only in the "look of
eagles" in their steady, quiet eyes. Each landed and went about his
ostensible business, interesting himself not at all in any of the
others.</p>
<p class="calibre3">Again the Boskonians declared their contempt of the Patrol by setting
the exact time at which the president was to be taken. Again the
appointed hour was midnight.</p>
<p class="calibre3">Vice Admiral Lensman Gerrond was, as Kinnison had intimated frequently,
somewhat of a brass hat. He did not, he simply could not believe that
his Base was as pregnable as the Co-ordinator had assumed it to be.
Kinnison, knowing that all ordinary defenses would be useless, had not
even mentioned them. Gerrond, unable to believe that his hitherto
invincible and invulnerable weapons and defenses were all of a sudden
useless, mustered them of his own volition.</p>
<p class="calibre3">All leaves had been canceled. Every detector, every beam, every device
of defense and of offense was fully manned. Every man was keyed up and
alert. And Gerrond, while the least bit apprehensive that something was
about to happen which was not in the book, was pretty sure in his stout
old war-dog's soul that he and his men had stuff enough.</p>
<p class="calibre3">At two minutes before midnight the armored president and his escorts
left Gerrond's private office. One minute later they were passing
the door of the specified room. A bomb exploded shatteringly behind
them, armored men rushed yelling out of a branch corridor in their
rear. Everybody stopped and turned to look. So, the hidden Kinnison
assured himself, did an unseen observer in an invisibly hovering,
three-dimensional hypercircle.</p>
<p class="calibre3">Kinnison threw the door open, flashed an explanatory thought at the
president, yanked him into the room and into the midst of a corps of
Lensmen armed with devices not usually encountered even in Patrol
bases. The door snapped shut and Kinnison stood where the president had
stood an instant before, clad in armor identical with that which the
president had worn. The exchange had required less than one second: it
had been observed by no one.</p>
<p class="calibre3">"QX, Gerrond and you fellows!" Kinnison drove the thought. "The
president is safe—I'm taking over. Double time straight ahead—hipe!
Get into the clear—give us a chance to use our stuff!"</p>
<p class="calibre3">The unarmored men broke into a run, and as they did so the door of Room
Twenty-four swung open and stayed open. Weapons snouted out, shoved by
armored men. Armored men and heavy weapons erupted from other doors and
from more branch corridors. The hypercircle, which was, in fact, the
terminus of a hyperspatial tube, began to thicken toward visibility.</p>
<p class="calibre3">It did not, however, materialize. Only by the intensest effort of
vision could it be discerned as the sheerest wisp, more tenuous than
the thinnest fog. The men within the ship, if ship it was, were visible
only as striations in air are visible, and no more to be made out
in detail. Instead of a full materialization, the only thing that
was or became solid or tangible was a dead-black thing which reached
purposefully outward and downward toward Kinnison, a thing combined of
tongs and coarse-meshed, heavy net.</p>
<p class="calibre3">Kinnison's DeLameters flamed at maximum intensity and minimum aperture.
Useless. The stuff was dureum; that unbelievably dense and ultimately
refractory synthetic which, saturated with pure force, is the only
known substance which can exist as an actuality both in normal space
and in that pseudospace which composes the hyperspatial tube. The
Lensman flicked on his neutralizer and shot away inertialess; but that
maneuver, too, had been foreseen. The Boskonian engineers matched every
move he made, within a split second after he made it; the tong-net
gripped and closed.</p>
<p class="calibre3">Semiportables flamed then—heavy stuff—but they might just as well
have remained cold. Their beams could not cut the dureum linkages; they
slid harmlessly <i class="calibre9">past</i>—not through—the wraithlike, figmental
invaders at whom they were timed. Kinnison was hauled aboard the
Boskonian vessel; its structure and its furnishings and its crew
becoming ever firmer and more substantial to his senses as he went from
normal into pseudospace.</p>
<p class="calibre3">As the pseudoworld became real, the reality of the base behind him
thinned into unreality. In seconds it disappeared utterly, and Kinnison
knew that to the senses of his fellow human beings he had vanished
without leaving a trace. This ship, though, was real enough. So were
his captors.</p>
<p class="calibre3">The net opened, dumping the Lensman ignominiously to the floor. Tractor
beams wrenched his blazing DeLameters out of his grasp—whether or not
hands and arms came with them was entirely his own lookout. Tractors
and pressors jerked him upright, slammed him against the steel wall of
the room, held him motionless against it.</p>
<p class="calibre3">Furiously he launched his ultimately lethal weapon, the
Worsel-designed, Thorndyke-built, mind-controlled projector of
thought-borne vibrations which decomposed the molecules without which
thought and life itself could not exist. Nothing happened. He explored,
finding that even his sense of perception was stopped a full foot away
from every part of every one of those humanoid bodies. He settled down
then and thought. A great light dawned; a shock struck sickeningly home.</p>
<p class="calibre3">No such elaborate and super-powered preparations would have been made
for the capture of any civilian. Presidents were old men, physically
weak and with no extraordinary powers of mind. No—this whole chain of
events had been according to plan—a high Boskonian's plan. Ruining a
planet was, of course, a highly desirable feature in itself, but it
could not have been the main feature.</p>
<p class="calibre3">Somebody with a real brain was out after the four Second-Stage Lensmen
and he wasn't fooling. And if Nadreck, Worsel, Tregonsee and himself
were all to disappear, the Patrol would know that it had been nudged.
But jet back—which of the four other than himself would have taken
that particular bait? Not one of them. Weren't they out after them,
too? Sure they were—they must be. Oh, if he could only warn them—but
after all, what good would it do? They had all warned each other
repeatedly to watch out for traps; all four had been constantly on
guard. What possible foresight could have avoided a snare set so
perfectly to match every detail of a man's physical and mental make-up?</p>
<p class="calibre3">But he wasn't licked yet. They had to know what he knew, how he had
done what he had done, whether or not he had any superiors and who they
were. Therefore they had had to take him alive, just as he had had to
take various Boskonian chiefs. And they'd find out that as long as he
was alive he'd be a dangerous buzzsaw to monkey with.</p>
<p class="calibre3">The captain, or whoever was in charge, would send for him; that was a
foregone conclusion. He would have to find out what it was that he had
caught; he would have to make a preliminary report of some kind. And
somebody would slip. One hundred percent vigilance was impossible, and
Kinnison would be on his toes to take advantage of that slip, whatever
or however slight it might be.</p>
<p class="calibre3">But the captors did not take Kinnison to the captain. Instead,
accompanied by half-a-dozen armored men, that worthy came to Kinnison.</p>
<p class="calibre3">"Start talking, fellow, and talk fast," the Boskonian directed crisply
in the lingua franca of deep space as the armored soldiers strode
out. "I want to know who you are, what you are, what you've done, and
everything about you and the Patrol. So talk—or do you want me to pull
you apart with these tractors, armor and all?"</p>
<p class="calibre3">Kinnison paid no attention, but drove at the commander with his every
mental force and weapon. Blocked. This ape too had a full-body,
full-coverage screen.</p>
<p class="calibre3">There was a switch, at the captain's hip, handy for finger-tip control.
If he could only move! It would be <i class="calibre9">so</i> easy to flip that switch!
Or if he could throw something, or make one of those other fellows
brush against him just right, or if the guy happened to sit down a
little too close to the arm of a chair, or if there were a pet animal
of any kind around, or a spider or a worm or even a gnat—</p>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// Section0006.vue?84dd9330